<script lang="ts">
	import { dldl, yyyy, zzzz } from "../store.svelte";
	import { toast } from "../lib/toast.svelte.ts";
	import { onMount, onDestroy } from "svelte";
	import { fly, slide } from "svelte/transition";
	import Fa from "svelte-fa";
	import {
		faTable,
		faChevronDown,
		faChevronRight,
		faDownload,
		faCopy,
		faCircleNotch,
		faListUl,
		faChartPie,
		faRuler,
		faBolt,
		faClipboard,
	} from "@fortawesome/free-solid-svg-icons";

	const url: string = yyyy.uri.api;
	// Track expanded tables
	let expandedTables = $state({});
	let expandedPhases = $state({});

	// Computed summary data
	let tableSummary = $derived(getTableSummary());
	let phaseSummary = $derived(getPhaseSummary());
	let kvaSummary = $derived(getKvaSummary());

	// Function to toggle expansion for a specific table
	function toggleTable(tableName) {
		expandedTables[tableName] = !expandedTables[tableName];
		expandedTables = { ...expandedTables }; // Force reactivity
	}

	// Function to toggle expansion for a specific phase group
	function togglePhase(phaseGroup) {
		expandedPhases[phaseGroup] = !expandedPhases[phaseGroup];
		expandedPhases = { ...expandedPhases }; // Force reactivity
	}

	// Function to calculate summary data by table for point features only
	function getTableSummary() {
		const summary = {};

		// Initialize with empty data if nrgzd data doesn't exist yet
		if (!yyyy.nrgzd?.findenergized?.features) return summary;

		// Count only point geometries by table
		yyyy.nrgzd.findenergized.features.forEach((feature) => {
			// Only process points
			if (feature.geometry.type !== "Point") return;

			const tableName = feature.properties.lw_table || "Unknown";
			const phase = feature.properties.phase || "Unknown";

			if (!summary[tableName]) {
				summary[tableName] = {
					total: 0,
					phases: {},
				};
			}

			summary[tableName].total++;

			if (!summary[tableName].phases[phase]) {
				summary[tableName].phases[phase] = 0;
			}

			summary[tableName].phases[phase]++;
		});

		return summary;
	}

	// Function to calculate summary by table and phase for line lengths
	function getPhaseSummary() {
		const summary = {
			byTable: {},
			totalLength: 0,
			totalByPhase: {}, // Add total by phase for all tables combined
		};

		// Get data from nrgzd instead of map.idnt
		if (!yyyy.nrgzd?.findenergized?.features) return summary;

		// Process each feature
		yyyy.nrgzd.findenergized.features.forEach((feature) => {
			// Skip points - we only want lines
			if (feature.geometry.type !== "LineString") return;

			const phase = feature.properties.phase || "Unknown";
			const length = feature.properties.issues?.length || 0;
			const lw_table = feature.properties.lw_table || "Unknown";

			// Initialize table if not exists
			if (!summary.byTable[lw_table]) {
				summary.byTable[lw_table] = {
					totalLength: 0,
					byPhase: {},
				};
			}

			// Initialize phase within table if not exists
			if (!summary.byTable[lw_table].byPhase[phase]) {
				summary.byTable[lw_table].byPhase[phase] = {
					length: 0,
				};
			}

			// Initialize total phase if not exists
			if (!summary.totalByPhase[phase]) {
				summary.totalByPhase[phase] = 0;
			}

			// Update table and phase summary
			summary.byTable[lw_table].totalLength += length;
			summary.byTable[lw_table].byPhase[phase].length += length;

			// Update total length and total by phase
			summary.totalLength += length;
			summary.totalByPhase[phase] += length;
		});

		return summary;
	}

	// Function to calculate KVA summary by phase
	function getKvaSummary() {
		const summary = {
			byPhase: {
				A: 0,
				B: 0,
				C: 0,
			},
			totalKVA: 0,
		};

		// Get data from nrgzd
		if (!yyyy.nrgzd?.findenergized?.features) return summary;

		// Process each point feature with voltamps
		yyyy.nrgzd.findenergized.features.forEach((feature) => {
			// Skip non-point features
			if (feature.geometry.type !== "Point") return;

			const voltamps = feature.properties.voltamps;

			// Skip if no voltamps data
			if (!voltamps) return;

			// Convert VA to kVA (divide by 1000) for each phase
			if (voltamps[0] && !isNaN(voltamps[0])) {
				const kva = voltamps[0] / 1000;
				summary.byPhase.A += kva;
				summary.totalKVA += kva;
			}

			if (voltamps[1] && !isNaN(voltamps[1])) {
				const kva = voltamps[1] / 1000;
				summary.byPhase.B += kva;
				summary.totalKVA += kva;
			}

			if (voltamps[2] && !isNaN(voltamps[2])) {
				const kva = voltamps[2] / 1000;
				summary.byPhase.C += kva;
				summary.totalKVA += kva;
			}
		});

		return summary;
	}

	// Format length to display nicely (with units and rounding)
	function formatLength(length) {
		if (length >= 1000) {
			return `${(length / 1000).toFixed(2)} km`;
		} else if (length >= 1) {
			return `${length.toFixed(2)} m`;
		} else {
			return `${(length * 100).toFixed(2)} cm`;
		}
	}

	// Format KVA with appropriate units
	function formatKVA(kva) {
		if (kva >= 1000) {
			return `${(kva / 1000).toFixed(2)} MVA`;
		} else {
			return `${kva.toFixed(2)} kVA`;
		}
	}

	let isDownloading: boolean = $state(false);

	let downloadXL = async () => {
		dldl("Starting spreadsheet download...");
		isDownloading = true;

		try {
			// Check if energized data exists
			if (!yyyy?.nrgzd?.findenergized) {
				dldl("No energized data available");
				toast.info(
					"Please run an energization analysis first before downloading the spreadsheet."
				);
				isDownloading = false;
				return;
			}

			const energizedData = yyyy.nrgzd.findenergized;
			dldl("Energized data:", JSON.stringify(energizedData));

			// Prepare headers
			const headers = {
				"Content-Type": "application/json",
				Authorization: "Bearer " + yyyy.sesh.logtoken.token,
				Accept: "*/*",
			};

			// Prepare request body based on whether it's device-based or coordinate-based
			let body = {
				wireident: yyyy.sesh.activeLivewire.wireident,
			};

			// Check if we have the info object with all parameters
			if (energizedData.info) {
				const info = energizedData.info;
				dldl("Info object:", JSON.stringify(info));

				// All parameters come from the info object
				body.direction = info.Direction;
				body.phase = info.lw_phase;

				// Check if it's coordinate-based (isPartial=true) or device-based (isPartial=false)
				if (info.isPartial === true) {
					// Coordinate-based - extract from "Energized From" object
					const energizedFrom = info["Energized From"];
					if (energizedFrom && energizedFrom.coords && energizedFrom.tablename) {
						// Parse the POINT coordinates
						const coordMatch = energizedFrom.coords.match(
							/POINT\(([^ ]+) ([^ )]+)\)/,
						);
						if (coordMatch) {
							body.x = parseFloat(coordMatch[1]);
							body.y = parseFloat(coordMatch[2]);
						}
						body.lw_table = energizedFrom.tablename;
					} else {
						dldl(
							"Missing coordinate data in Energized From:",
							JSON.stringify(energizedFrom),
						);
						toast.info("Missing coordinate data for download.");
						isDownloading = false;
						return;
					}

					dldl("Using coordinate-based download, body:", JSON.stringify(body));
				} else if (info.isPartial === false) {
					// Device-based - add lw_id
					body.lw_id = info["Energized From"];

					dldl("Using device-based download, body:", JSON.stringify(body));
				} else {
					dldl("isPartial flag not set properly, info:", JSON.stringify(info));
					toast.info("Unable to determine download type from energized data.");
					isDownloading = false;
					return;
				}
			} else {
				dldl("Missing energized info object");
				toast.info("Energized data is incomplete. Please run the analysis again.");
				isDownloading = false;
				return;
			}

			// Make the API call
			const response = await fetch(`${url}/rpc/downloadspreadsheet`, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (response.ok) {
				// Get the binary data
				const data = await response.arrayBuffer();
				dldl("Received spreadsheet data, size:", data.byteLength);

				// Create blob and download
				const blob = new Blob([data], {
					type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				});
				const downloadUrl = URL.createObjectURL(blob);

				// Create download link
				const link = document.createElement("a");
				link.href = downloadUrl;
				link.download = `energized_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
				link.click();

				// Cleanup
				URL.revokeObjectURL(downloadUrl);
				dldl("Download initiated successfully");
			} else {
				const errorText = await response.text();
				dldl("Download failed:", response.status, errorText);
				toast.error(`Failed to download spreadsheet: ${response.statusText}`);
			}
		} catch (error) {
			dldl("Error during download:", error);
			toast.error("An error occurred while downloading the spreadsheet.");
		} finally {
			isDownloading = false;
		}
	};

	// Function to copy data as CSV to clipboard
	let copyCSV = () => {
		if (!yyyy.map.idnt || yyyy.map.idnt.length === 0) {
			alert("No data available to copy");
			return;
		}

		// Get all unique property keys
		const allKeys = new Set();
		yyyy.map.idnt.forEach((item) => {
			if (item.properties) {
				Object.keys(item.properties).forEach((key) => allKeys.add(key));
			}
		});

		const headers = Array.from(allKeys);
		let csvContent = headers.join(",") + "\n";

		// Add data rows
		yyyy.map.idnt.forEach((item) => {
			const row = headers.map((header) => {
				let value = item.properties?.[header] || "";
				// Escape quotes and wrap values with commas in quotes
				if (String(value).includes(",") || String(value).includes('"')) {
					value = `"${String(value).replace(/"/g, '""')}"`;
				}
				return value;
			});
			csvContent += row.join(",") + "\n";
		});

		// Copy to clipboard
		navigator.clipboard
			.writeText(csvContent)
			.then(() => {
				toast.success("Data copied to clipboard as CSV");
			})
			.catch((err) => {
				console.log("Failed to copy: ", err);
				toast.warning("Failed to copy data to clipboard");
			});
	};

	// Function to copy summary to clipboard
	let copySummary = () => {
		if (!yyyy.nrgzd?.findenergized?.features) {
			alert("No summary data available to copy");
			return;
		}

		let summaryText = "";

		// Add origin information from info
		if (yyyy.nrgzd?.findenergized?.info) {
			const info = yyyy.nrgzd.findenergized.info;
			const direction = info.Direction || "UNKNOWN";
			const feeder = info.shadow_row?.feederid || "Unknown";
			const table = info.shadow_row?.lw_table || "Unknown";
			let originDesc = `Find energized devices ${direction}`;

			// Add the starting point details
			if (info["Energized From"]) {
				originDesc += ` ${info["Energized From"]}`;
			}
			if (info.shadow_row?.lw_table) {
				originDesc += ` on table ${table}`;
			}
			if (info.lw_table_pkid) {
				originDesc += ` (ID: ${info.lw_table_pkid})`;
			}

			// Add phase information if available
			if (info.lw_phase) {
				originDesc += ` - Phase: ${info.lw_phase}`;
			}

			summaryText += originDesc + "\n";
			summaryText += "=".repeat(originDesc.length) + "\n\n";
		} else {
			summaryText += "Data Summary\n";
			summaryText += "=============\n\n";
		}

		// Add Point Features Summary
		if (Object.keys(tableSummary).length > 0) {
			summaryText += "Point Features:\n";
			for (const [tableName, data] of Object.entries(tableSummary)) {
				summaryText += `  ${tableName}: ${data.total} total\n`;
				for (const [phaseName, count] of Object.entries(data.phases)) {
					summaryText += `    Phase ${phaseName}: ${count}\n`;
				}
			}
			summaryText += "\n";
		}

		// Add Line Length Summary
		if (Object.keys(phaseSummary.byTable).length > 0) {
			summaryText += "Line Lengths:\n";
			for (const [tableName, tableData] of Object.entries(phaseSummary.byTable)) {
				summaryText += `  ${tableName}: ${formatLength(tableData.totalLength)}\n`;
				for (const [phaseName, phaseData] of Object.entries(tableData.byPhase)) {
					const percent = ((phaseData.length / tableData.totalLength) * 100).toFixed(
						1,
					);
					summaryText += `    Phase ${phaseName}: ${formatLength(phaseData.length)} (${percent}%)\n`;
				}
			}
			summaryText += `\nTotal Energized Length: ${formatLength(phaseSummary.totalLength)}\n\n`;
		}

		// Add KVA Summary
		if (kvaSummary.totalKVA > 0) {
			summaryText += "Power Capacity:\n";
			summaryText += `  Total: ${formatKVA(kvaSummary.totalKVA)}\n`;
			if (kvaSummary.byPhase.A > 0) {
				const percent = (
					(kvaSummary.byPhase.A / kvaSummary.totalKVA) *
					100
				).toFixed(1);
				summaryText += `  Phase A: ${formatKVA(kvaSummary.byPhase.A)} (${percent}%)\n`;
			}
			if (kvaSummary.byPhase.B > 0) {
				const percent = (
					(kvaSummary.byPhase.B / kvaSummary.totalKVA) *
					100
				).toFixed(1);
				summaryText += `  Phase B: ${formatKVA(kvaSummary.byPhase.B)} (${percent}%)\n`;
			}
			if (kvaSummary.byPhase.C > 0) {
				const percent = (
					(kvaSummary.byPhase.C / kvaSummary.totalKVA) *
					100
				).toFixed(1);
				summaryText += `  Phase C: ${formatKVA(kvaSummary.byPhase.C)} (${percent}%)\n`;
			}
		}

		// Copy to clipboard
		navigator.clipboard
			.writeText(summaryText)
			.then(() => {
				toast.success("Summary copied to clipboard");
			})
			.catch((err) => {
				console.log("Failed to copy summary: ", err);
				toast.warning("Failed to copy summary to clipboard");
			});
	};

	onMount(() => {
		// Initialize any necessary functionality
	});

	onDestroy(() => {
		// Clean up if necessary
	});
</script>

<div class="summary-container" in:fly={{ y: -50, duration: 200 }}>
	<div class="summary-header">
		<h3>Data Summary</h3>
		<div class="summary-actions">
			{#if !isDownloading}
				<button
					class="action-button"
					on:click={downloadXL}
					title="Download as Excel"
				>
					<Fa icon={faDownload} />
				</button>
			{:else}
				<button class="action-button" title="Download in progress">
					<Fa icon={faCircleNotch} spin />
				</button>
			{/if}
			<button
				class="action-button"
				on:click={copyCSV}
				title="Copy raw data as CSV"
			>
				<Fa icon={faCopy} />
			</button>
			<button
				class="action-button"
				on:click={copySummary}
				title="Copy summary text"
			>
				<Fa icon={faClipboard} />
			</button>
		</div>
	</div>

	<div class="summary-content">
		<!-- Single Summary Section -->
		<div class="summary-section">
			<div class="table-summary">
				<!-- Point Features -->
				{#if Object.keys(tableSummary).length > 0}
					{#each Object.entries(tableSummary) as [tableName, data]}
						<div class="table-item">
							<div class="table-header" on:click={() => toggleTable(tableName)}>
								<span class="expand-icon">
									<Fa
										icon={expandedTables[tableName] ? faChevronDown : faChevronRight}
									/>
								</span>
								<span class="table-icon"><Fa icon={faTable} /></span>
								<span class="table-name">{tableName}</span>
								<span class="table-count">{data.total}</span>
							</div>

							{#if expandedTables[tableName]}
								<div class="phase-list" transition:slide={{ duration: 200 }}>
									{#each Object.entries(data.phases) as [phaseName, count]}
										<div class="phase-item">
											<span
												class="phase-name phase-indicator phase-{phaseName.toLowerCase()}"
												>{phaseName}</span
											>
											<span class="phase-count">{count}</span>
										</div>
									{/each}
								</div>
							{/if}
						</div>
					{/each}
				{/if}

				<!-- Line Lengths -->
				{#if yyyy.nrgzd?.findenergized?.features && Object.keys(phaseSummary.byTable).length > 0}
					{#each Object.entries(phaseSummary.byTable) as [tableName, tableData]}
						<div class="table-item">
							<div
								class="table-header"
								on:click={() => toggleTable(tableName + "-length")}
							>
								<span class="expand-icon">
									<Fa
										icon={expandedTables[tableName + "-length"]
											? faChevronDown
											: faChevronRight}
									/>
								</span>
								<span class="table-icon"><Fa icon={faRuler} /></span>
								<span class="table-name">{tableName}</span>
								<span class="kva-badge">{formatLength(tableData.totalLength)}</span>
							</div>

							{#if expandedTables[tableName + "-length"]}
								<div class="phase-list" transition:slide={{ duration: 200 }}>
									{#each Object.entries(tableData.byPhase) as [phaseName, phaseData]}
										<div class="phase-item">
											<span
												class="phase-name phase-indicator phase-{phaseName.toLowerCase()}"
												>{phaseName}</span
											>
											<div class="phase-kva-details">
												<span class="kva-value">{formatLength(phaseData.length)}</span>
												<span class="kva-percent"
													>({((phaseData.length / tableData.totalLength) * 100).toFixed(
														1,
													)}%)</span
												>
											</div>
										</div>
									{/each}
								</div>
							{/if}
						</div>
					{/each}

					<!-- Total Energized Length -->
					<div class="table-item total-length-item">
						<div class="table-header" on:click={() => toggleTable("total-length")}>
							<span class="expand-icon">
								<Fa
									icon={expandedTables["total-length"] ? faChevronDown : faChevronRight}
								/>
							</span>
							<span class="table-icon"><Fa icon={faRuler} /></span>
							<span class="table-name">Total Energized Length</span>
							<span class="kva-badge total-badge"
								>{formatLength(phaseSummary.totalLength)}</span
							>
						</div>

						{#if expandedTables["total-length"]}
							<div class="phase-list" transition:slide={{ duration: 200 }}>
								{#each Object.entries(phaseSummary.totalByPhase) as [phaseName, length]}
									<div class="phase-item">
										<span
											class="phase-name phase-indicator phase-{phaseName.toLowerCase()}"
											>{phaseName}</span
										>
										<div class="phase-kva-details">
											<span class="kva-value">{formatLength(length)}</span>
											<span class="kva-percent"
												>({((length / phaseSummary.totalLength) * 100).toFixed(1)}%)</span
											>
										</div>
									</div>
								{/each}
							</div>
						{/if}
					</div>
				{/if}

				<!-- Total Power Capacity -->
				{#if kvaSummary.totalKVA > 0}
					<div class="table-item">
						<div class="table-header" on:click={() => toggleTable("kva-summary")}>
							<span class="expand-icon">
								<Fa
									icon={expandedTables["kva-summary"] ? faChevronDown : faChevronRight}
								/>
							</span>
							<span class="table-icon"><Fa icon={faBolt} /></span>
							<span class="table-name">Total Power Capacity</span>
							<span class="kva-badge total-badge">{formatKVA(kvaSummary.totalKVA)}</span>
						</div>

						{#if expandedTables["kva-summary"]}
							<div class="phase-list" transition:slide={{ duration: 200 }}>
								{#if kvaSummary.byPhase.A > 0}
									<div class="phase-item">
										<span class="phase-name phase-indicator phase-a">A</span>
										<div class="phase-kva-details">
											<span class="kva-value">{formatKVA(kvaSummary.byPhase.A)}</span>
											<span class="kva-percent"
												>({((kvaSummary.byPhase.A / kvaSummary.totalKVA) * 100).toFixed(
													1,
												)}%)</span
											>
										</div>
									</div>
								{/if}
								{#if kvaSummary.byPhase.B > 0}
									<div class="phase-item">
										<span class="phase-name phase-indicator phase-b">B</span>
										<div class="phase-kva-details">
											<span class="kva-value">{formatKVA(kvaSummary.byPhase.B)}</span>
											<span class="kva-percent"
												>({((kvaSummary.byPhase.B / kvaSummary.totalKVA) * 100).toFixed(
													1,
												)}%)</span
											>
										</div>
									</div>
								{/if}
								{#if kvaSummary.byPhase.C > 0}
									<div class="phase-item">
										<span class="phase-name phase-indicator phase-c">C</span>
										<div class="phase-kva-details">
											<span class="kva-value">{formatKVA(kvaSummary.byPhase.C)}</span>
											<span class="kva-percent"
												>({((kvaSummary.byPhase.C / kvaSummary.totalKVA) * 100).toFixed(
													1,
												)}%)</span
											>
										</div>
									</div>
								{/if}
							</div>
						{/if}
					</div>
				{/if}

				<!-- Show empty state if no data at all -->
				{#if Object.keys(tableSummary).length === 0 && (!yyyy.nrgzd?.findenergized?.features || Object.keys(phaseSummary.byTable).length === 0) && kvaSummary.totalKVA === 0}
					<div class="empty-state">No data available</div>
				{/if}
			</div>
		</div>
	</div>
</div>

<style>
	.summary-container {
		background-color: #fff;
		border-radius: 4px;
		border: 1px solid #dbdbdb;
		margin-bottom: 16px;
		box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
		max-height: 70vh;
		overflow-y: auto;
	}

	.summary-header {
		padding: 12px 16px;
		border-bottom: 1px solid #eee;
		display: flex;
		justify-content: space-between;
		align-items: center;
		position: sticky;
		top: 0;
		background-color: #fff;
		z-index: 2;
	}

	.summary-header h3 {
		margin: 0;
		color: #363636;
		font-size: 1.2rem;
	}

	.summary-actions {
		display: flex;
		gap: 8px;
	}

	.action-button {
		background-color: transparent;
		border: none;
		color: #3273dc;
		cursor: pointer;
		padding: 4px 8px;
		border-radius: 4px;
		transition: background-color 0.2s;
	}

	.action-button:hover {
		background-color: rgba(50, 115, 220, 0.1);
	}

	.summary-content {
		padding: 12px 16px;
	}

	.summary-section {
		margin-bottom: 0px;
	}

	.empty-state {
		text-align: center;
		padding: 24px 0;
		color: #999;
		font-style: italic;
	}

	.summary-stats {
		display: flex;
		gap: 16px;
		margin-bottom: 16px;
		flex-wrap: wrap;
	}

	.stat-item {
		background-color: #f5f5f5;
		padding: 8px 12px;
		border-radius: 4px;
		display: flex;
		flex-direction: column;
		width: 100%;
	}

	.stat-label {
		font-size: 0.9rem;
		color: #666;
	}

	.stat-value {
		font-size: 1.1rem;
		font-weight: 500;
	}

	.line-summary {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.kva-summary {
		margin-bottom: 20px;
	}

	.kva-badge {
		background-color: #3273dc;
		color: white;
		border-radius: 12px;
		padding: 2px 8px;
		font-size: 0.8rem;
		min-width: 24px;
		text-align: center;
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.phase-kva-details {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.kva-value {
		font-weight: 500;
	}

	.kva-percent {
		font-size: 0.8rem;
		color: #666;
	}

	.phase-details {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		margin-top: 8px;
	}

	.phase-kva-item {
		display: flex;
		align-items: center;
		gap: 8px;
		background-color: #f5f5f5;
		padding: 6px 12px;
		border-radius: 4px;
	}

	.kva-value {
		font-weight: 500;
	}

	.phase-group {
		border: 1px solid #eee;
		border-radius: 4px;
		overflow: hidden;
		padding: 16px;
		background-color: white;
	}

	.phase-header {
		padding: 10px 12px;
		background-color: #f5f5f5;
		display: flex;
		align-items: center;
		cursor: pointer;
		transition: background-color 0.2s;
	}

	.phase-header:hover {
		background-color: #eaeaea;
	}

	.expand-icon {
		margin-right: 8px;
		width: 16px;
	}

	.phase-indicator {
		padding: 2px 8px;
		border-radius: 4px;
		font-weight: 500;
		margin-right: 0;
		min-width: 32px;
		text-align: center;
		display: inline-block;
	}

	/* Phase color coding */
	.phase-a,
	.phase-bar-segment.phase-a {
		background-color: rgba(152, 32, 19, 0.2);
		color: #982013;
	}

	.phase-bar-segment.phase-a {
		background-color: rgba(152, 32, 19, 0.7);
	}

	.phase-b,
	.phase-bar-segment.phase-b {
		background-color: rgba(25, 149, 12, 0.2);
		color: #19950c;
	}

	.phase-bar-segment.phase-b {
		background-color: rgba(25, 149, 12, 0.7);
	}

	.phase-c,
	.phase-bar-segment.phase-c {
		background-color: rgba(24, 51, 139, 0.2);
		color: #18338b;
	}

	.phase-bar-segment.phase-c {
		background-color: rgba(24, 51, 139, 0.7);
	}

	.phase-ab {
		background: linear-gradient(
			135deg,
			rgba(152, 32, 19, 0.2) 50%,
			rgba(25, 149, 12, 0.2) 50%
		);
		color: #333;
	}

	.phase-ac {
		background: linear-gradient(
			135deg,
			rgba(152, 32, 19, 0.2) 50%,
			rgba(24, 51, 139, 0.2) 50%
		);
		color: #333;
	}

	.phase-bc {
		background: linear-gradient(
			135deg,
			rgba(25, 149, 12, 0.2) 50%,
			rgba(24, 51, 139, 0.2) 50%
		);
		color: #333;
	}

	.phase-abc {
		background: linear-gradient(
			135deg,
			rgba(152, 32, 19, 0.2) 33%,
			rgba(25, 149, 12, 0.2) 33%,
			rgba(25, 149, 12, 0.2) 66%,
			rgba(24, 51, 139, 0.2) 66%
		);
		color: #333;
	}

	.phase-unknown {
		background-color: #f0f0f0;
		color: #666;
	}

	.phase-stats {
		display: flex;
		gap: 12px;
		margin-left: auto;
		align-items: center;
	}

	.count-badge,
	.length-badge {
		background-color: #3273dc;
		color: white;
		border-radius: 12px;
		padding: 2px 8px;
		font-size: 0.8rem;
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.length-badge {
		background-color: #23d160;
	}

	.phase-details {
		padding: 12px;
		background-color: #fff;
		border-top: 1px dashed #eee;
	}

	.detail-section {
		margin-bottom: 16px;
	}

	.detail-section h5 {
		font-size: 0.9rem;
		margin: 0 0 8px 0;
		color: #666;
	}

	.detail-item {
		display: flex;
		justify-content: space-between;
		padding: 4px 0 4px 12px;
		border-bottom: 1px dashed #eee;
	}

	.detail-item:last-child {
		border-bottom: none;
	}

	.detail-name {
		font-size: 0.9rem;
	}

	.detail-stats {
		display: flex;
		gap: 8px;
	}

	.detail-count,
	.detail-length {
		font-size: 0.8rem;
		background-color: #f5f5f5;
		padding: 2px 6px;
		border-radius: 4px;
	}

	.table-summary {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.table-item {
		/* border: 1px solid #eee; */
		/* border-radius: 4px; */
		overflow: hidden;
	}

	.total-length-item {
		border-top: 2px solid #e0e0e0;
		margin-top: 8px;
		padding-top: 8px;
	}

	.total-badge {
		font-weight: bold;
		background-color: #395a99;
	}

	.table-header {
		padding: 8px 12px;
		background-color: #f5f5f5;
		display: flex;
		align-items: center;
		cursor: pointer;
		transition: background-color 0.2s;
	}

	.table-header:hover {
		background-color: #eaeaea;
	}

	.table-icon {
		margin-right: 8px;
		color: black;
	}

	.table-name {
		flex-grow: 1;
		font-weight: 500;
	}

	.table-count {
		background-color: #3273dc;
		color: white;
		border-radius: 12px;
		padding: 2px 8px;
		font-size: 0.8rem;
		min-width: 24px;
		text-align: center;
	}

	.phase-list {
		padding: 8px 12px;
		background-color: #fff;
	}

	.phase-item {
		display: flex;
		justify-content: space-between;
		padding: 4px 0 4px 32px;
		border-bottom: 1px dashed #eee;
	}

	.phase-item:last-child {
		border-bottom: none;
	}

	.phase-name {
		font-size: 0.9rem;
	}

	.phase-count {
		background-color: #3273dc;
		color: white;
		border-radius: 12px;
		padding: 1px 8px;
		font-size: 0.8rem;
		min-width: 24px;
		text-align: center;
	}

	/* Add these color styles to the phase names in the Point Features section */
	.phase-item .phase-indicator {
		display: inline-block;
		padding: 2px 8px;
		border-radius: 4px;
		font-weight: normal;
		margin-right: 0;
		min-width: 40px;
		text-align: center;
	}
</style>
